import dummyImg from "assets/images/users/user-dummy-img.jpg";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { Eye, EyeOff, ImagePlus, Loader } from "lucide-react";
import AuthIcon from "pages/AuthenticationInner/AuthIcon";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "Common/SafeLink";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { resetRegisterFlag } from "slices/thunk";

// Formik validation
import { useFormik as useFormic } from "formik";
import * as Yup from "yup";

// Image
import logoDark from "assets/images/logo-dark.png";
import logoLight from "assets/images/logo-light.png";
import { toast } from "react-toastify";
import { useSignUp } from "./useSignUp";

const Register = () => {
  document.title = "Register | Betaplay Merchant Dashboard";
  const [showPassword, setShowPassword] = useState(false);

  const { createUser, isCreating } = useSignUp();
  const dispatch = useDispatch();
  const navigation = useNavigate();
  // Image
  const [selectedImage, setSelectedImage] = useState();
  const [previewImage, setPreviewImage] = useState();

  const selectRegister = createSelector(
    (state) => state.Register,
    (register) => ({
      success: register.success,
    })
  );

  const handleImageChange = (event) => {
    const fileInput = event.target;
    if (fileInput.files && fileInput.files.length > 0) {
      const file = fileInput.files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        validation.setFieldValue("image", e.target.result);
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      validation.setFieldValue("image", file);
    }
  };

  const { success } = useSelector(selectRegister);

  const validation = useFormic({
    enableReinitialize: true,
    initialValues: {
      email: "",
      displayName: "",
      password: "",
      image: "",
      phoneNumber: "",
      merchantName: "",
    },
    validationSchema: Yup.object({
      phoneNumber: Yup.string()
        .matches(
          /^\+234\d{10}$/,
          "Phone number must be in the format: +234XXXXXXXXXX (e.g., +2349029409151)"
        )
        .required("Please Enter Your Phone Number"),
      email: Yup.string().email().required("Please Enter Your Email"),
      displayName: Yup.string().required("Please Enter Your Display Name"),
      image: Yup.string(),
      password: Yup.string().required("Please Enter Your Password"),
      merchantName: Yup.string().required("Please Select Your Merchant"),
    }),
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append("phoneNumber", values.phoneNumber);
        formData.append("displayName", values.displayName);
        formData.append("email", values.email);
        formData.append("password", values.password);
        formData.append("merchantName", values.merchantName);
        if (selectedImage) formData.append("file", selectedImage);

        // if (selectedImage) {
        //   const storage = getStorage();
        //   const storageRef = ref(storage, `images/${values.displayName}`);
        //   const uploadTask = uploadBytesResumable(storageRef, selectedImage);

        //   await new Promise((resolve, reject) => {
        //     uploadTask.on(
        //       "state_changed",
        //       (snapshot) => {
        //         // Handle upload progress if needed
        //         const progress =
        //           (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        //       },
        //       (error) => {
        //         reject(error);
        //       },
        //       async () => {
        //         try {
        //           const downloadURL = await getDownloadURL(
        //             uploadTask.snapshot.ref
        //           );
        //           formData.append("image", downloadURL);
        //           resolve();
        //         } catch (error) {
        //           reject(error);
        //         }
        //       }
        //     );
        //   });
        // } else {
        //   formData.append("image", "");
        // }

        // Log the FormData values
        // formData.forEach((value, key) => {
        //   console.log(`${key}: ${value}`);
        // });
        createUser(formData);
      } catch (error) {
        console.error("Error during sign-up:", error);
        toast.error("Failed to create merchant. Please try again.");
      }
    },
  });

  React.useEffect(() => {
    if (success) {
      navigation("/login");
    }

    setTimeout(() => {
      dispatch(resetRegisterFlag());
    }, 3000);
  }, [dispatch, success, navigation]);

  React.useEffect(() => {
    const bodyElement = document.body;

    bodyElement.classList.add(
      "flex",
      "items-center",
      "justify-center",
      "min-h-screen",
      "py-16",
      "lg:py-10",
      "bg-slate-50",
      "dark:bg-zink-800",
      "dark:text-zink-100",
      "font-public"
    );

    return () => {
      bodyElement.classList.remove(
        "flex",
        "items-center",
        "justify-center",
        "min-h-screen",
        "py-16",
        "lg:py-10",
        "bg-slate-50",
        "dark:bg-zink-800",
        "dark:text-zink-100",
        "font-public"
      );
    };
  }, []);

  return (
    <React.Fragment>
      <div className="relative">
        <AuthIcon />

        <div className="mb-0 w-screen lg:w-[500px] card shadow-lg border-none shadow-slate-100 relative">
          <div className="!px-10 !py-12 card-body">
            <Link to="/">
              <img
                src={logoLight}
                alt=""
                className="hidden h-6 mx-auto dark:block"
              />
              <img
                src={logoDark}
                alt=""
                className="block h-6 mx-auto dark:hidden"
              />
            </Link>

            <div className="mt-8 text-center">
              <h4 className="mb-1 text-custom-500 dark:text-custom-500">
                Create your merchant account
              </h4>
            </div>

            <form
              action="/"
              className="mt-10"
              id="registerForm"
              onSubmit={(event) => {
                event.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              <div className="mb-3">
                <div className="relative size-24 mx-auto mb-4 rounded-full shadow-md bg-slate-100 profile-user dark:bg-zink-500">
                  <img
                    src={previewImage || validation.values.image || dummyImg}
                    alt=""
                    className="object-cover w-full h-full rounded-full user-profile-image"
                  />
                  <div className="absolute bottom-0 flex items-center justify-center size-8 rounded-full ltr:right-0 rtl:left-0 profile-photo-edit">
                    <input
                      id="profile-img-file-input"
                      name="profile-img-file-input"
                      type="file"
                      accept="image/*"
                      className="hidden profile-img-file-input"
                      onChange={handleImageChange}
                    />
                    <label
                      htmlFor="profile-img-file-input"
                      className="flex items-center justify-center size-8 bg-white rounded-full shadow-lg cursor-pointer dark:bg-zink-600 profile-photo-edit"
                    >
                      <ImagePlus className="size-4 text-slate-500 fill-slate-200 dark:text-zink-200 dark:fill-zink-500" />
                    </label>
                  </div>
                </div>
                {validation.touched.image && validation.errors.image ? (
                  <p className="text-red-400">{validation.errors.image}</p>
                ) : null}
              </div>
              <div className="mb-3">
                <label
                  htmlFor="displayName-field"
                  className="inline-block mb-2 text-base font-medium"
                >
                  Display Name
                </label>
                <input
                  type="text"
                  id="displayName-field"
                  name="displayName"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Enter Display Name for users"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.displayName || ""}
                />
                {validation.touched.displayName &&
                validation.errors.displayName ? (
                  <div
                    id="displayName-error"
                    className="mt-1 text-sm text-red-500"
                  >
                    {validation.errors.displayName}
                  </div>
                ) : null}
              </div>
              <div className="mb-3">
                <label
                  htmlFor="email-field"
                  className="inline-block mb-2 text-base font-medium"
                >
                  Phone Number
                </label>
                <input
                  type="text"
                  id="phoneNumber-field"
                  name="phoneNumber"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Enter phoneNumber"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.phoneNumber || ""}
                />
                {validation.touched.phoneNumber &&
                validation.errors.phoneNumber ? (
                  <div
                    id="phoneNumber-error"
                    className="mt-1 text-sm text-red-500"
                  >
                    {validation.errors.phoneNumber}
                  </div>
                ) : null}
              </div>
              <div className="mb-3">
                <label
                  htmlFor="email-field"
                  className="inline-block mb-2 text-base font-medium"
                >
                  Email
                </label>
                <input
                  type="text"
                  id="email-field"
                  name="email"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Enter email"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.email || ""}
                />
                {validation.touched.email && validation.errors.email ? (
                  <div id="email-error" className="mt-1 text-sm text-red-500">
                    {validation.errors.email}
                  </div>
                ) : null}
              </div>
              <div className="mb-3">
                <label
                  htmlFor="merchantName-field"
                  className="inline-block mb-2 text-base font-medium"
                >
                  Merchant
                </label>
                <select
                  id="merchantName-field"
                  name="merchantName"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.merchantName || ""}
                >
                  <option value="" disabled>
                    Select merchant
                  </option>
                  <option value="Madina Lottery">Madina Lottery</option>
                  <option value="Betatips">Betatips</option>
                  <option value="Betavow">Betavow</option>
                  <option value="Jackpot Connect">Jackpot Connect</option>
                  <option value="National Health Lottery">National Health Lottery</option>
                  <option value="Twingle">Twingle</option>
                  <option value="BetaCare">BetaCare</option>
                </select>
                {validation.touched.merchantName && validation.errors.merchantName ? (
                  <div id="merchantName-error" className="mt-1 text-sm text-red-500">
                    {validation.errors.merchantName}
                  </div>
                ) : null}
              </div>
              <div className="mb-3 relative">
                <label
                  htmlFor="password"
                  className="inline-block mb-2 text-base font-medium"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    className="form-input w-full pr-10 border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    placeholder="Enter password"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.password || ""}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-300"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
                {validation.touched.password && validation.errors.password ? (
                  <div
                    id="password-error"
                    className="mt-1 text-sm text-red-500"
                  >
                    {validation.errors.password}
                  </div>
                ) : null}
              </div>
              <p className="italic text-15 text-slate-500 dark:text-zink-200">
                By registering you agree to the Betaplay{" "}
                <a href="#!" className="underline">
                  Terms of Use
                </a>
              </p>
              <div className="mt-10">
                <button
                  type="submit"
                  className="w-full text-white transition-all justify-items-center duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                >
                  {isCreating ? <Loader className="animate-spin" /> : "Sign Up"}
                </button>
              </div>

              <div className="mt-10 text-center">
                <p className="mb-0 text-slate-500 dark:text-zink-200">
                  Already have an account ?{" "}
                  <Link
                    to="/login"
                    className="font-semibold underline transition-all duration-150 ease-linear text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500"
                  >
                    Login
                  </Link>{" "}
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Register;
